import Vue from 'vue';
import axios from 'axios';
import Router from '../router';
import { Helpers } from '@/tools/helpers';
import { clearToken } from '@/store/api';
import { AttachmentGetParams, BusinessProfile, BusinessProfileResponse, CaseStudy, IProductService, ISurveyResponse, Project, SupplyChains } from '@/store/models';

const Qs = require('qs');

const CancelToken = axios.CancelToken;
let cancel: any = null;

// Moving multipart/form-data content-type apis here... so that economxapi is set to use application/json, 
// this will fix the issue where saving roi/mr/timer after creating a project was failing bcoz the create 
// project requires multipart/form-data content-type and this changes the default headers

export const filesapi = axios.create({
  baseURL: process.env.VUE_APP_API,
  headers: { 'Content-Type': 'multipart/form-data' }
});

export const octatestream = axios.create({
  baseURL: process.env.VUE_APP_API,
  headers: { 'Content-Type': 'application/octet-stream' }
});

// Format nested params correctly
filesapi.interceptors.request.use((config) => {
  config.paramsSerializer = (params) => {
    // Qs is already included in the Axios package
    return Qs.stringify(params, {
      arrayFormat: 'brackets',
      encode: false,
    });
  };

  return config;
});

filesapi.interceptors.response.use(
  (success) => success,
  (error) => {
    switch (error.response.status) {
      case 401:
        //  Token has expired
        Vue.swal('Logged Out', 'Your session has expired. Please login again.').then((result) => {
          clearToken().then((res) => {
            Router.push('/');
          });
        });
        return null;
      case 403:
        //  Permission Denied
        Vue.swal('Authorisation Error', 'You do not have permissions to perform this action').then((result) => {
          Router.push('/');
        });
        return null;
      case 422:
        //  Unprocessable entity
        Vue.swal(
          'Error',
          error.response.data && error.response.data.description
            ? `${error.response.data.description}`
            : 'Something went wrong, please try again later or contact our support team',
        ).then((result) => {
          return result;
        });
        return null;
      case 500:
        // Any other server error
        Vue.swal(
          'Error',
          error.response.data && error.response.data.description
            ? `${error.response.data.description}`
            : 'Something went wrong, please try again later or contact our support team',
        ).then((result) => {
          return result;
        });
        return null;
      default:
        console.log('error', error);
        return error;
    }
  },
);


// Format nested params correctly
octatestream.interceptors.request.use((config) => {
  config.paramsSerializer = (params) => {
    // Qs is already included in the Axios package
    return Qs.stringify(params, {
      arrayFormat: 'brackets',
      encode: false,
    });
  };

  return config;
});

octatestream.interceptors.response.use(
  (success) => success,
  (error) => {
    switch (error.response.status) {
      case 401:
        //  Token has expired
        Vue.swal('Logged Out', 'Your session has expired. Please login again.').then((result) => {
          clearToken().then((res) => {
            Router.push('/');
          });
        });
        return null;
      case 403:
        //  Permission Denied
        Vue.swal('Authorisation Error', 'You do not have permissions to perform this action').then((result) => {
          Router.push('/');
        });
        return null;
      case 422:
        //  Unprocessable entity
        Vue.swal(
          'Error',
          error.response.data && error.response.data.description
            ? `${error.response.data.description}`
            : 'Something went wrong, please try again later or contact our support team',
        ).then((result) => {
          return result;
        });
        return null;
      case 500:
        // Any other server error
        Vue.swal(
          'Error',
          error.response.data && error.response.data.description
            ? `${error.response.data.description}`
            : 'Something went wrong, please try again later or contact our support team',
        ).then((result) => {
          return result;
        });
        return null;
      default:
        console.log('error', error);
        return error;
    }
  },
);


// Converts object to FormData
function getFormData(object: any) {
  const formData = new FormData();
  Object.keys(object).forEach((key) => {
    formData.append(key, object[key]);
  });
  return formData;
}

// ***********************
// Attachment Routes
// ***********************

export async function downloadFile(params: AttachmentGetParams, progressCallback: any): Promise<any> {
  octatestream.defaults.onDownloadProgress = progressCallback;
  const response = await octatestream.get(`/attachments/${params.id}`, {
    responseType: 'blob',
    params,
    cancelToken: new CancelToken(function executor(c) {
      // An executor function receives a cancel function as a parameter
      cancel = c;
    }),
  });
  return response.data;
}

export async function createMultipleAttachments(payload: any): Promise<any> {
  filesapi.defaults.onUploadProgress = payload.progressEvent;
  const response = await filesapi.post(
    `/attachments/create_multiple?service=${payload.service}`,
    Helpers.ObjectToFormData(payload.data, 'data'),
    {
      cancelToken: new CancelToken(function executor(c) {
        cancel = c;
      }),
    },
  );
  return response.data.attachments;
}

export async function createOrganization(payload: any): Promise<BusinessProfile> {
  const response = await filesapi.post(
    `/business/profile/organizations`,
    Helpers.ObjectToFormData(payload, 'organization'),
  );
  return response.data as BusinessProfileResponse;
}

export async function updateBusinessProfile(payload: any): Promise<BusinessProfile> {
  const response = await filesapi.put(
    `/business/profile/organizations/${payload.id}?section=${payload.section}`,
    Helpers.ObjectToFormData(payload.profile, 'business'),
  );
  return response.data as BusinessProfileResponse;
}

export async function createCaseStudy(caseStudy: CaseStudy): Promise<any> {
  return await filesapi.post(`/business/case_studies/`, getFormData(caseStudy));
}

export async function updateCaseStudy(caseStudy: CaseStudy): Promise<any> {
  return await filesapi.put(`/business/case_studies/${caseStudy.id}`, getFormData(caseStudy));
}

export async function updateProductService(productService: IProductService): Promise<any> {
  return await filesapi.put(
    `/business/product_services/${productService.id}`,
    Helpers.ObjectToFormData(productService, 'product_service'),
  );
}

export async function createProductService(productService: IProductService): Promise<any> {
  return await filesapi.post(`/business/product_services/`, Helpers.ObjectToFormData(productService));
}

export async function createProject(project: SupplyChains): Promise<Project> {
  const response = await filesapi.post(`/projects`, Helpers.ObjectToFormData(project, 'project'));
  return response.data as Project;
}

export async function updateProject(project: SupplyChains): Promise<Project> {
  const response = await filesapi.put(`/projects/${project.id}`, Helpers.ObjectToFormData(project, 'project'));
  return response.data as Project;
}

export async function createSurvey(survey: any): Promise<any> {
  const response = await filesapi.post('/ecx_surveys/surveys/', Helpers.ObjectToFormData(survey, 'survey'));
  return response.data as any;
}

export async function updateSurvey(survey: any): Promise<any> {
  const response = await filesapi.put(`/ecx_surveys/surveys/${survey.id}?which=survey`, Helpers.ObjectToFormData(survey, 'survey'));
  return response.data as any;
}

export async function createSurveyResponse(surveyresponse: ISurveyResponse): Promise<any> {
  const response = await filesapi.post(`/ecx_surveys/survey_responses`, Helpers.ObjectToFormData(surveyresponse, 'survey_response'));
  return response.data as any;
}

export function cancelRequest() {
  console.log('cancelled');
  if (cancel) {
    cancel();
  }


}

export async function exportMrData(mr: any): Promise<any> {
  const response = await octatestream.get(`/export_mr_data`, {
    responseType: 'blob',
    params: { id: mr.id, market_request: { project_id: mr.project_id } },
    cancelToken: new CancelToken(function executor(c) {
      // An executor function receives a cancel function as a parameter
      cancel = c;
    }),
  });
  return response.data;
}
